/**
 * ContactFormPage - ESS Contact Form Implementation
 *
 * This component provides a complete contact form with:
 * - ESS Design System components (@plone/components based)
 * - PageHeaderSimple for header section
 * - Form validation
 * - hCaptcha integration (Plone/Volto native)
 * - Email submission via Plone backend
 * - Success/error feedback
 * - Responsive layout matching ESS website style
 */

import React, { useState, useRef } from 'react';
import { Helmet } from '@plone/volto/helpers';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Button } from '../../Atoms/ESSButton/Button';
import { TextField } from '../../Atoms/ESSTextField/TextField';
import { TextAreaField } from '../../Atoms/ESSTextAreaField/TextAreaField';
import { PageHeaderBackground } from 'volto-essvoltocomponents/components/Molecules';
import {
  ContentNarrow,
  ContentWide,
  ContentWrapper,
  PageSection,
} from 'volto-essvoltocomponents/components/Layout';
import heroImage from '../../../images/hero_bg.png';
import './ContactFormPage.scss';

const ContactFormPage = () => {
  const recaptchaRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: null,
  });

  const [errors, setErrors] = useState({});
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle change for @plone/components API (receives value directly)
  const handleFieldChange = (fieldName) => (value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: '',
      }));
    }
  };

  const handleCaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleCaptchaExpire = () => {
    setRecaptchaToken(null);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    /*console.log('🔵 handleSubmit called');
    console.log('📝 Form data:', formData);*/

    const isValid = validateForm();
    console.log('✅ Form valid?', isValid);
    console.log('❌ Errors:', errors);

    if (!isValid) {
      console.log('⚠️ Validation failed, stopping submission');
      return;
    }

    // Validate hCaptcha
    if (!recaptchaToken) {
      setFormStatus({
        submitting: false,
        submitted: false,
        error: 'Please complete the captcha verification.',
      });
      return;
    }

    setFormStatus({ submitting: true, submitted: false, error: null });

    try {
      // Send email via browser view endpoint through Volto API proxy
      const response = await fetch('/++api++/@@contact-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          from: formData.email,
          subject: formData.subject,
          message: formData.message,
        }),
      });

      if (response.ok) {
        setFormStatus({ submitting: false, submitted: true, error: null });
        setFormData({ name: '', email: '', subject: '', message: '' });
        setRecaptchaToken(null);
        if (recaptchaRef.current) {
          recaptchaRef.current.resetCaptcha();
        }
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setFormStatus({
        submitting: false,
        submitted: false,
        error:
          'Failed to send message. Please try again or contact us directly.',
      });
    }
  };

  return (
    <div className="contact-form-page">
      <Helmet title="Contact" />

      <PageHeaderBackground
        variant="default"
        title="Contact"
        description="For all enquiries, please email us using the form below"
        backgroundImage={heroImage}
      />
      <PageSection paddingBottom="var(--ess-space-7xl)">
        <ContentWrapper>
          <ContentWide alignment="left">
            <ContentNarrow className="contact-form-page">
              <div className="contact-form-container">
                <h2 className="form-section-title">Get in touch</h2>

                {formStatus.submitted && (
                  <div className="form-message form-success">
                    <p>
                      <strong>Thank you for your message!</strong>
                    </p>
                    <p>We will get back to you as soon as possible.</p>
                  </div>
                )}

                {formStatus.error && (
                  <div className="form-message form-error">
                    <p>
                      <strong>Error:</strong> {formStatus.error}
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="contact-form">
                  <TextField
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleFieldChange('name')}
                    placeholder="Name"
                    isInvalid={!!errors.name}
                    errorMessage={errors.name}
                    isDisabled={formStatus.submitting}
                    size="md"
                  />

                  <TextField
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleFieldChange('email')}
                    placeholder="Email address"
                    isInvalid={!!errors.email}
                    errorMessage={errors.email}
                    isDisabled={formStatus.submitting}
                    size="md"
                  />

                  <TextField
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleFieldChange('subject')}
                    placeholder="Subject"
                    isInvalid={!!errors.subject}
                    errorMessage={errors.subject}
                    isDisabled={formStatus.submitting}
                    size="md"
                  />

                  <TextAreaField
                    name="message"
                    value={formData.message}
                    onChange={handleFieldChange('message')}
                    placeholder="Message"
                    rows={6}
                    isInvalid={!!errors.message}
                    errorMessage={errors.message}
                    isDisabled={formStatus.submitting}
                    size="md"
                  />

                  {/* hCaptcha Integration */}
                  <div className="captcha-container">
                    <HCaptcha
                      ref={recaptchaRef}
                      sitekey={
                        process.env.RAZZLE_HCAPTCHA_SITE_KEY ||
                        '93b8d7da-69c0-4dd0-8242-0ed8be8c2d7e'
                      }
                      onVerify={handleCaptchaChange}
                      onExpire={handleCaptchaExpire}
                    />
                  </div>

                  <div className="form-actions">
                    <Button
                      variant="positive"
                      size="md"
                      loading={formStatus.submitting}
                      isDisabled={formStatus.submitting}
                      onPress={(e) => {
                        handleSubmit(e);
                      }}
                    >
                      {formStatus.submitting ? 'SENDING...' : 'SEND'}
                    </Button>
                  </div>
                </form>
              </div>
            </ContentNarrow>
          </ContentWide>
        </ContentWrapper>
      </PageSection>
    </div>
  );
};

export default ContactFormPage;
